import * as d3 from 'd3';
import { Component, h } from 'preact';
import { wordwrap } from 'd3-jetpack';

import { showPrecinctsReporting } from '../endpoints';

import { customNumFormat, customBarNumFormat } from '../utils/format-numbers';

class MiniBarChart extends Component {
  constructor() {
    super();

    this.state = {
      width: null,
      height: 90,
      bodyWidth: null,
      breakPoint: 768,
    };

    this.createGraphic = this.createGraphic.bind(this);
    this.redrawGraphic = this.redrawGraphic.bind(this);
    this.getContainerWidth = this.getContainerWidth.bind(this);
    this.onResize = this.onResize.bind(this);
    this.resizeFrame = null;
  }

  componentDidMount() {
    const {
      className,
      categoryNumbers,
      candidatesRender,
      candidates,
      officeSlug,
      maxNum,
    } = this.props;
    let { width, bodyWidth } = this.getContainerWidth(officeSlug);

    this.setState({ width: width, bodyWidth: bodyWidth }, () => {
      this.createGraphic(
        className,
        categoryNumbers,
        candidatesRender,
        candidates,
        officeSlug,
        maxNum
      );
    });

    window.addEventListener('resize', this.onResize);
  }

  componentDidUpdate() {
    const {
      className,
      categoryNumbers,
      candidatesRender,
      candidates,
      officeSlug,
      maxNum,
    } = this.props;

    this.createGraphic(
      className,
      categoryNumbers,
      candidatesRender,
      candidates,
      officeSlug,
      maxNum
    );
  }

  createGraphic(
    className,
    categoryNumbers,
    candidatesRender,
    candidates,
    officeSlug,
    maxNum
  ) {
    let { width, bodyWidth } = this.getContainerWidth(officeSlug);

    let { height, breakPoint } = this.state;

    d3.selectAll(`.bar-chart-${className}-${officeSlug} svg`).remove();

    const container = d3.select(`.bar-chart-${className}-${officeSlug}`);

    let newcand = candidates.map((obj) => ({
      ...obj,
      last_name: obj.name.split(' ').slice(-1).join(' '),
      category_votes: categoryNumbers[1][0][obj.id],
    }));

    // chart setup
    // Widen the left margin when a name label (like "Rosenthal") is longer
    // than the default leaves room for; 12 is the label's gap from the bars
    const labelWidth = this.measureLabels(container, newcand);
    let margin = {
      top: 0,
      bottom: 20,
      right: 30,
      left: Math.max(70, Math.ceil(labelWidth) + 12),
    };

    (width = width - margin.left - margin.right),
      (height =
        categoryNumbers[1][0]['votes_still_to_count'] > 0 && showPrecinctsReporting ==  true
          ? 90 - margin.top - margin.bottom
          : 68 - margin.top - margin.bottom);

    const isMobile = bodyWidth < breakPoint;

    let svg = container
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom);

    let diagonalStripePattern = svg
      .append('defs')
      .append('pattern')
      .attr('id', 'diagonal-stripe')
      .attr('patternUnits', 'userSpaceOnUse')
      .attr('patternTransform', 'rotate(45 0 0)')
      .attr('width', 6)
      .attr('height', 6);

    diagonalStripePattern
      .append('rect')
      .attr('width', 6)
      .attr('height', 6)
      .style('fill', '#d9d9d9');

    diagonalStripePattern
      .append('line')
      .attr('x1', 0)
      .attr('y1', 0)
      .attr('x2', 0)
      .attr('y2', 6)
      .style('stroke', 'black')
      .style('stroke-width', 0.5);

    let g = svg
      .append('g')
      .attr('class', `bar-chart-${className}-${officeSlug}-group`)
      .attr('width', width)
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // if (categoryNumbers[1][0]['votes_still_to_count'] > 0 && showPrecinctsReporting == true && isNaN(categoryNumbers[1][0]['votes_still_to_count']) == false) {
    //   newcand.push({
    //     category_votes: categoryNumbers[1][0]['votes_still_to_count'],
    //     last_name: 'Votes left to count',
    //     party: 'A-Votes',
    //   });
    // }

    newcand = newcand.sort(function (a, b) {
      return a.party.localeCompare(b.party);
    });

    let altMaxNum = 2963326;

    var x = d3.scaleLinear().range([0, width]).domain([0, isNaN(maxNum) == true ? altMaxNum : maxNum]);

    var y = d3
      .scaleBand()
      .domain(
        newcand.map(function (entry) {
          return entry.last_name;
        })
      )
      .range([height, 0])
      .padding(0.4);

    // light gray bar underneath
    let graybars = g
      .selectAll('.bar-gray')
      .data(newcand)
      .enter()
      .append('rect')
      .attr('class', 'bar-light-gray')
      .attr('fill', '#F5F5F5')
      .attr('width', x(isNaN(maxNum) == true ? altMaxNum : maxNum))
      .attr('y', (d) => y(d.last_name))
      .attr('height', y.bandwidth());

    // add the x Axis
    g.append('g')
      .attr('transform', 'translate(0,' + height + ')')
      .call(
        d3.axisBottom(x).ticks(3).tickFormat(customNumFormat).tickSize(-height)
      )
      .attr('class', 'axis axis--x')
      .selectAll('text')
      .attr('dy', '0.99em');

    // add the y Axis
    g.append('g')
      .attr('class', 'axis axis--y')
      .style('text-anchor', 'end')
      .call(d3.axisLeft(y))
      .selectAll('text')
      .attr('y', 5)
      .attr('x', -5)
      .attr('text-anchor', 'end')
      .text('')
      .tspans((d) => wordwrap(d, 10))
      .attr('x', -12);

    d3.selectAll('tspan')
      .nodes()
      .forEach((d) => {
        if (d.textContent == 'Votes left ') {
          d.textContent = d.textContent.replace(/[ \t]+$/g, '');
        }
      });

    // append the rectangles for the bar chart
    let bars = g
      .selectAll('.bar')
      .data(newcand)
      .enter()
      .append('rect')
      .attr('class', 'bar-category')
      .attr('fill', (d) => {
        let foundCandidate = candidatesRender.find((a) => a.id == d.id);
        if (foundCandidate) return foundCandidate.color;
        else return '#bdbdbd';
      })
      .attr('width', (d) => x(d.category_votes))
      .attr('y', (d) => y(d.last_name))
      .attr('height', y.bandwidth());

    g.selectAll('.bar')
      .data(newcand)
      .enter()
      .append('text')
      .attr('x', (d) => x(d.category_votes) + 3)
      .attr('text-anchor', 'start')
      .attr('y', (d) => y(d.last_name) + y.bandwidth() / 2)
      .attr('dy', '.35em')
      .text((d) => customBarNumFormat(d.category_votes));
  }


  // Width of the longest y-axis label line, measured in a throwaway axis group
  // so it picks up the same .axis--y font styles as the real labels
  measureLabels(container, candidates) {
    const svg = container.append('svg');
    const axis = svg.append('g').attr('class', 'axis axis--y');
    let longest = 0;

    candidates.forEach((d) => {
      wordwrap(d.last_name, 10).forEach((line) => {
        const text = axis.append('text').text(line).node();
        longest = Math.max(longest, text.getComputedTextLength());
      });
    });

    svg.remove();
    return longest;
  }

  getContainerWidth(office_slug) {
    const bodyWidth = document.body.offsetWidth;
    const grid = this.base && this.base.parentElement.closest('.category-inline-grid');

    if (!grid) {
      const showcase = document.querySelector('.contest-showcase__contest');
      return { width: showcase.offsetWidth - 80, bodyWidth };
    }

    const gap = parseFloat(getComputedStyle(grid).columnGap) || 0;
    const tinyMap = grid.firstElementChild;
    const width = Math.floor(
      grid.parentElement.clientWidth - tinyMap.offsetWidth - gap
    );

    return { width, bodyWidth };
  }

  redrawGraphic() {
    const { officeSlug } = this.props;
    let { width, bodyWidth } = this.getContainerWidth(officeSlug);

    this.setState({ width: width, bodyWidth: bodyWidth });
  }

  onResize() {
    if (this.resizeFrame) return;

    this.resizeFrame = window.requestAnimationFrame(() => {
      this.resizeFrame = null;
      this.redrawGraphic();
    });
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
    if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);
  }

  render() {
    const { className, officeSlug } = this.props;

    return (
      <div
        class={`category-bar-chart bar-chart-${className}-${officeSlug}`}
      ></div>
    );
  }
}

export default MiniBarChart;